import { create } from 'zustand';
import type { ViewMode } from '@/map/map-setup';
import type { CoverageFrame } from '@/map/coverage/coverage-frame';

/** A point fly-to: pan to a coordinate with an optional zoom. */
interface PointFlyTo {
  lng: number;
  lat: number;
  zoom?: number;
}

/** A coverage-framing fly-to: frame a satellite together with its footprint. */
interface FrameFlyTo {
  frame: CoverageFrame;
}

/** Target for a flyTo camera animation (point or coverage frame). */
type FlyToTarget = PointFlyTo | FrameFlyTo;

/** 3D camera state */
interface Camera3D {
  range: number;
  tilt: number;
  heading: number;
}

/** Map interaction state */
interface MapState {
  lastClick: { lng: number; lat: number } | null;
  flyToTarget: FlyToTarget | null;
  activeStyleId: string;
  viewMode: ViewMode;
  camera3d: Camera3D;
  /** Bumped to request a camera reset to the default overhead view. */
  resetNonce: number;
  /** Whether road/place labels overlay the 3D photorealistic imagery (HYBRID vs SATELLITE). */
  labelsVisible: boolean;
  setLastClick: (coords: { lng: number; lat: number }) => void;
  setFlyTo: (target: FlyToTarget) => void;
  clearFlyTo: () => void;
  setActiveStyleId: (id: string) => void;
  setViewMode: (mode: ViewMode) => void;
  setCamera3D: (camera: Partial<Camera3D>) => void;
  /** Reset the camera to the default overhead view. */
  resetView: () => void;
  /** Toggle the 3D label overlay (HYBRID ↔ SATELLITE). */
  toggleLabels: () => void;
}

/** Store for map interaction state */
export const useMapStore = create<MapState>((set) => ({
  lastClick: null,
  flyToTarget: null,
  activeStyleId: 'dark',
  viewMode: '3d',
  camera3d: { range: 10_000_000, tilt: 0, heading: 0 },
  resetNonce: 0,
  labelsVisible: true,

  setLastClick: (coords) => set({ lastClick: coords }),
  setFlyTo: (target) => set({ flyToTarget: target }),
  clearFlyTo: () => set({ flyToTarget: null }),
  setActiveStyleId: (id) => set({ activeStyleId: id }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setCamera3D: (camera) =>
    set((state) => ({ camera3d: { ...state.camera3d, ...camera } })),
  resetView: () => set((state) => ({ resetNonce: state.resetNonce + 1 })),
  toggleLabels: () => set((state) => ({ labelsVisible: !state.labelsVisible })),
}));
